import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/lib/site";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "surface-glass border-b border-emerald-900/50 py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3 group" data-testid="navbar-logo">
          <img src={COMPANY.logo} alt="Jahzara Phenix crest" className="w-11 h-11 object-contain rounded-lg" />
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-sm tracking-[0.2em] text-emerald-50 uppercase">Jahzara Phenix</div>
            <div className="text-[10px] tracking-[0.3em] text-gold uppercase">Holding Company</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm text-slate-300 hover:text-gold transition-colors tracking-wide relative after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-gold after:transition-all hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link to="/login" className="hidden sm:block">
            <Button
              data-testid="navbar-login-button"
              className="bg-transparent border border-gold/60 text-gold hover:bg-gold hover:text-emerald-950 rounded-full px-5 h-10 tracking-wide transition-all"
            >
              <ShieldCheck className="w-4 h-4 mr-2" /> Executive Login
            </Button>
          </Link>
          <button
            className="lg:hidden text-emerald-100"
            onClick={() => setOpen(!open)}
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden surface-glass border-t border-emerald-900/50 mt-3 px-6 py-5 flex flex-col gap-4">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-slate-200 hover:text-gold">
              {l.label}
            </a>
          ))}
          <Link to="/login" onClick={() => setOpen(false)}>
            <Button className="w-full bg-gold text-emerald-950 rounded-full">Executive Login</Button>
          </Link>
        </div>
      )}
    </header>
  );
}
